import { useCallback, useEffect, useRef } from 'react'

import {
  CircleLoader,
  EmptyState,
  ErrorAlert,
  Space,
  TextButton,
} from '@procurify/ui'
import { useTheme } from 'styled-components'

import { useProcurifyIntl } from '@webapp/hooks'
import { ReportAccessGrantTag } from './components/ReportAccessGrantTag'
import messages from './messages'
import { ClearAllButtonStyled } from './styles/ClearAllButtonStyled'
import { type ReportAccessGrantTagViewModel } from '../../types'

const getRemoveButtonId = (grantId: number) =>
  `report-access-grant-remove-${grantId}`

export interface IReportAccessGrantListViewProps {
  grantTags: ReportAccessGrantTagViewModel[]
  clearAllCount: number
  showInitialLoader: boolean
  showErrorState: boolean
  showEmptyState: boolean
  showClearAll: boolean
  isClearAllDisabled: boolean
  onRemoveGrant: (grantId: number) => void
  onClearAll: () => void
  onRetry: () => void
}

export const ReportAccessGrantListView = ({
  grantTags,
  clearAllCount,
  showInitialLoader,
  showErrorState,
  showEmptyState,
  showClearAll,
  isClearAllDisabled,
  onRemoveGrant,
  onClearAll,
  onRetry,
}: IReportAccessGrantListViewProps) => {
  const intl = useProcurifyIntl()
  const theme = useTheme()
  const removedTagRef = useRef<{ grantId: number; index: number }>()

  const handleRemoveGrant = useCallback(
    (grantId: number) => {
      removedTagRef.current = {
        grantId,
        index: grantTags.findIndex(({ id }) => id === grantId),
      }
      onRemoveGrant(grantId)
    },
    [grantTags, onRemoveGrant]
  )

  // The optimistic revoke unmounts the focused remove button; hand focus to
  // the tag now at its position (the next one, else the previous one). The
  // Feature Set takes it to the user picker when no tag is left. Focus the
  // user moved elsewhere meanwhile is left alone.
  useEffect(() => {
    const removedTag = removedTagRef.current
    if (!removedTag || grantTags.some(({ id }) => id === removedTag.grantId)) {
      return
    }

    removedTagRef.current = undefined
    if (document.activeElement && document.activeElement !== document.body) {
      return
    }

    const neighbourTag =
      grantTags[Math.min(removedTag.index, grantTags.length - 1)]
    if (neighbourTag) {
      document.getElementById(getRemoveButtonId(neighbourTag.id))?.focus()
    }
  }, [grantTags])

  if (showInitialLoader) {
    return (
      <Space direction='horizontal' justify='center'>
        <CircleLoader color={theme.colors.text.secondary} />
      </Space>
    )
  }

  if (showErrorState) {
    return (
      <ErrorAlert
        action={
          <TextButton type='button' onClick={onRetry}>
            {intl.formatMessage(messages.retryButtonLabel)}
          </TextButton>
        }
      >
        {intl.formatMessage(messages.errorStateMessage)}
      </ErrorAlert>
    )
  }

  return (
    <Space>
      {showClearAll && (
        <div>
          <ClearAllButtonStyled
            type='button'
            disabled={isClearAllDisabled}
            onClick={onClearAll}
          >
            {intl.formatMessage(messages.clearAllLabel, {
              count: clearAllCount,
            })}
          </ClearAllButtonStyled>
        </div>
      )}

      {showEmptyState ? (
        <EmptyState
          header={intl.formatMessage(messages.emptyStateHeader)}
          content={intl.formatMessage(messages.emptyStateContent)}
        />
      ) : (
        <Space direction='horizontal' size='small' flexWrap>
          {grantTags.map((grantTag) => (
            <ReportAccessGrantTag
              key={grantTag.id}
              grantId={grantTag.id}
              removeButtonId={getRemoveButtonId(grantTag.id)}
              userFullName={grantTag.userFullName}
              isHighlighted={grantTag.isHighlighted}
              onRemove={handleRemoveGrant}
            />
          ))}
        </Space>
      )}
    </Space>
  )
}

ReportAccessGrantListView.displayName = 'ReportAccessGrantListView'
